import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import GuestPicker from "@/components/GuestPicker/GuestPicker";
import StayDatesPicker from "@/components/StayDatesPicker/StayDatesPicker";
import StaySearch from "@/model/StaySearch";
import "@/components/SearchBar/SearchBar.scss";

interface SearchBarProps {
  initialValue: StaySearch;
  onSearch: (search: StaySearch) => void;
}

function SearchBar({ initialValue, onSearch }: SearchBarProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<StaySearch>(initialValue);
  // Only check-in picked is a normal step while choosing dates: complain once the guest searches.
  const [submitted, setSubmitted] = useState(false);

  const onlyOneDate = (value.checkIn === "") !== (value.checkOut === "");
  const invalidRange =
    value.checkIn !== "" &&
    value.checkOut !== "" &&
    value.checkOut <= value.checkIn;

  const showDateError = submitted && (onlyOneDate || invalidRange);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    if (!onlyOneDate && !invalidRange) {
      onSearch(value);
    }
  };

  return (
    <form className="search-bar" onSubmit={handleSubmit} role="search">
      <div className="search-bar__fields">
        <div className="search-bar__field search-bar__field--destination">
          <label htmlFor="destination">{t("search.destinationLabel")}</label>
          <input
            id="destination"
            type="text"
            value={value.destination}
            onChange={(event) =>
              setValue((current) => ({ ...current, destination: event.target.value }))
            }
            placeholder={t("search.destinationPlaceholder")}
            autoComplete="off"
          />
        </div>

        <StayDatesPicker
          fieldClassName="search-bar__field"
          checkIn={value.checkIn}
          checkOut={value.checkOut}
          onChange={(checkIn, checkOut) =>
            setValue((current) => ({ ...current, checkIn, checkOut }))
          }
        />

        <GuestPicker
          id="guests"
          className="search-bar__field search-bar__field--guests"
          label={t("search.guestsLabel")}
          value={value.guests}
          onChange={(guests) => setValue((current) => ({ ...current, guests }))}
          allowEmpty
        />

        <button type="submit" className="search-bar__submit">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <span>{t("search.submit")}</span>
        </button>
      </div>

      {showDateError && (
        <p className="search-bar__error" role="alert">
          {invalidRange ? t("search.invalidRange") : t("search.bothDates")}
        </p>
      )}
    </form>
  );
}

export default SearchBar;
export type { SearchBarProps };
